import { jsxs, jsx } from "react/jsx-runtime";
import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import "../Button/Button.js";
import "../InputControl/InputControl.js";
import "../InputControl/Input/InputBase.js";
import "../PhoneInputControl/PhoneInputControl.js";
import "../PhoneInputControl/PhoneInput/PhoneInput.js";
import "../IconButton/IconButton.js";
import "../common/ControlBody.js";
import "@mui/material";
import "../SelectControl/Select/context/SelectContext.js";
import "../Dialog/Dialog.js";
import "../IconButtonNew/IconButton.js";
import "../../icons/24/Action/Close.js";
import "../../icons/24/Arrow/ChevronLeft.js";
import "../SelectControl/Select/components/SelectInput/SelectInput.js";
import { DATA_TEST_ID_INHERITED } from "../../constants/DATA_TEST_ID_INHERITED.js";
import "../SelectControl/Select/MultiSelect/context/MultiSelectContext.js";
import "../../icons/24/System/Check.js";
import "../../icons/24/Arrow/ChevronDown.js";
import "../TextareaControl/TextareaControl.js";
import "../SearchControl/SearchControl.js";
import "../../icons/24/System/Search.js";
import "../FormattedPhone/FormattedPhoneNumber.js";
import "../Avatar/styles.js";
import "../CheckboxGroup/Checkbox/styles.js";
import "../CheckboxGroup/styles.js";
import "../RadioGroup/Radio/styles.js";
import "../RadioGroup/styles.js";
import "../RatingButton/styles.js";
import "../FloatingActionButton/styles.js";
import "../SortingButton/styles.js";
import "../SquareButton/styles.js";
import "../Toggle/styles.js";
import "../Dialog/StyledDialog.js";
import "../Chips/Chip/styles.js";
import "../ResultMarksNumber/styles.js";
import "../Progress/ProgressCircle/styles.js";
import "../Loaders/LoaderCircle/styles.js";
import "../Loaders/LoaderLine/styles.js";
import "../SegmentedControl/styles.js";
import "../ResultMarksText/styles.js";
import "../Link/styles.js";
import "../Tag/Tag.js";
import "../DatePicker/constants.js";
import "../DatePicker/components/RangePicker/context/DateRangePickerContext.js";
import "../DatePicker/components/RangePicker/context/DateRangePickerContextActions.js";
import "../DatePicker/shared/fragments/CalendarCell/CalendarCell.js";
import "../../external/classnames/index.js";
import { Scrollbar } from "../Scrollbar/Scrollbar.js";
import "../DatePicker/shared/fragments/MonthYearSelector/YearSelector.js";
import "../DatePicker/components/DatePicker/context/DatePickerContext.js";
import "../DatePicker/components/DatePicker/context/DatePickerContextActions.js";
import "../MobileBottomSheet/MobileBottomSheet.js";
import "../List/ListItem/ListItem.js";
import "../Nowrap.js";
import "../List/ListItemTree/TreeListItem.js";
import "../List/ListItemTree/components/Label.js";
const VirtualList = ({
  children,
  data: list,
  estimatedItemHeight = 50,
  sx,
  ...props
}) => {
  const containerRef = useRef(null), [scrollWindowSize, setScrollWindowSize] = useState(1e3), [scrollTop, setScrollTop] = useState(0), [itemHeights, setItemHeights] = useState(
    () => new Array(list.length).fill(estimatedItemHeight)
  ), itemHeightsRef = useRef(itemHeights);
  useEffect(() => {
    itemHeightsRef.current = itemHeights;
  }, [itemHeights]), useEffect(() => {
    setItemHeights((prev) => {
      const newHeights = prev.slice(0, list.length);
      for (; newHeights.length < list.length; )
        newHeights.push(estimatedItemHeight);
      return newHeights;
    });
  }, [list.length, estimatedItemHeight]);
  const cumulativeHeights = useMemo(() => {
    const arr = new Array(list.length + 1).fill(0);
    for (let i = 0; i < list.length; i++)
      arr[i + 1] = arr[i] + itemHeights[i];
    return arr;
  }, [itemHeights, list.length]), totalHeight = cumulativeHeights[cumulativeHeights.length - 1], handleYResize = useCallback((height) => {
    setScrollWindowSize(height);
  }, []), handleScrollY = useCallback((e) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []), findStartIndex = useCallback(
    (scrollPosition) => {
      let low = 0, high = list.length;
      for (; low < high; ) {
        const mid = low + high >>> 1;
        cumulativeHeights[mid] <= scrollPosition ? low = mid + 1 : high = mid;
      }
      return Math.max(0, low - 1);
    },
    [cumulativeHeights, list.length]
  ), { startIndex, endIndex, topOffset, bottomOffset } = useMemo(() => {
    if (list.length === 0)
      return { startIndex: 0, endIndex: -1, topOffset: 0, bottomOffset: 0 };
    const buffer = 10, viewportBottom = scrollTop + scrollWindowSize, start = findStartIndex(scrollTop);
    let end = start;
    for (; end < list.length && cumulativeHeights[end + 1] < viewportBottom; )
      end++;
    const bufferedStart = Math.max(0, start - buffer), bufferedEnd = Math.min(list.length - 1, end + buffer), topOffset2 = cumulativeHeights[bufferedStart], bottomOffset2 = totalHeight - cumulativeHeights[bufferedEnd + 1];
    return {
      startIndex: bufferedStart,
      endIndex: bufferedEnd,
      topOffset: topOffset2,
      bottomOffset: bottomOffset2
    };
  }, [
    list.length,
    cumulativeHeights,
    totalHeight,
    scrollTop,
    scrollWindowSize,
    findStartIndex
  ]), startIndexRef = useRef(startIndex);
  startIndexRef.current = startIndex;
  const measureItem = useCallback((index, node) => {
    if (node) {
      const newHeight = node.getBoundingClientRect().height;
      newHeight !== itemHeightsRef.current[index + startIndexRef.current] && setItemHeights((prev) => {
        const updated = [...prev];
        return updated[index + startIndexRef.current] = newHeight, updated;
      });
    }
  }, []), visibleData = useMemo(
    () => list.slice(startIndex, endIndex + 1),
    [list, startIndex, endIndex]
  );
  return /* @__PURE__ */ jsxs(
    Scrollbar,
    {
      "data-test-id": DATA_TEST_ID_INHERITED,
      ...props,
      onYResize: handleYResize,
      sx,
      options: { suppressScrollX: !0 },
      shadow: !0,
      suppressScrollX: !0,
      onScroll: handleScrollY,
      containerRef: (el) => containerRef.current = el,
      id: "scroll_container",
      style: { position: "relative", height: "100%", overflowY: "auto" },
      children: [
        /* @__PURE__ */ jsx("div", { style: { height: topOffset, width: "100%" } }, "top"),
        children(visibleData, measureItem, startIndex),
        /* @__PURE__ */ jsx("div", { style: { height: bottomOffset, width: "100%" } }, "bottom")
      ]
    }
  );
};
export {
  VirtualList
};
//# sourceMappingURL=VirtualList.js.map
